/**
 * Step node — white card with a gold left border (Section 5).
 *
 * Title uses Source Serif Pro (the workflow display font); action chips
 * are colour-coded per kind, matching the spec:
 *   email      → blue
 *   whatsapp   → emerald
 *   sms        → slate
 *   internal_task → amber
 *
 * The "category" badge sits on the right edge of the header so a viewer
 * can scan the type at a glance.
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { ActionSpec, StepCategory } from '@mana/shared';
import type { StepRfNode } from '../layout.js';

export function StepNode({ data }: NodeProps<StepRfNode>) {
  return (
    <div
      className="rounded-lg border border-slate-200 border-l-4 border-l-brand-gold-500 bg-white px-3 py-2 shadow-sm"
      style={{ minWidth: 240, maxWidth: 280 }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-sm font-semibold text-brand-navy-800"
            style={{ fontFamily: '"Source Serif Pro", Georgia, serif' }}
          >
            {data.label}
          </div>
          <div className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-slate-400">
            {data.stepKey}
          </div>
        </div>
        <CategoryBadge category={data.category} />
      </div>

      {data.actions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {data.actions.slice(0, 6).map((a, i) => (
            <ActionChip key={i} action={a} />
          ))}
        </div>
      )}

      {data.guard && (
        <div className="mt-1.5 inline-flex max-w-full items-center gap-1 truncate rounded-md bg-brand-navy-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-navy-700 ring-1 ring-brand-navy-200">
          <span aria-hidden="true">⚙</span>
          <span className="truncate">guarded</span>
        </div>
      )}

      {/* Incoming and outgoing handles. Connect is disabled in read-only mode. */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        style={{ background: '#94A7BE', width: 8, height: 8, border: '2px solid white' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ background: '#B8965A', width: 8, height: 8, border: '2px solid white' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function CategoryBadge({ category }: { category: StepCategory }) {
  const palette: Record<StepCategory, string> = {
    NOTIFY: 'bg-sky-50 text-sky-700 ring-sky-200',
    WAIT: 'bg-slate-100 text-slate-700 ring-slate-200',
    BRANCH: 'bg-violet-50 text-violet-700 ring-violet-200',
    TASK: 'bg-amber-50 text-amber-700 ring-amber-200',
    TERMINAL: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 ${palette[category]}`}
    >
      {category}
    </span>
  );
}

function ActionChip({ action }: { action: ActionSpec }) {
  const palette: Record<ActionSpec['kind'], string> = {
    email: 'bg-blue-50 text-blue-700 ring-blue-200',
    whatsapp: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    sms: 'bg-slate-100 text-slate-700 ring-slate-300',
    internal_task: 'bg-amber-50 text-amber-700 ring-amber-200',
  };
  const label =
    action.kind === 'email'
      ? `email · ${action.to}`
      : action.kind === 'whatsapp'
        ? `whatsapp · ${action.to}`
        : action.kind === 'sms'
          ? `sms · ${action.to}`
          : `task · ${action.assignee}`;
  return (
    <span
      className={`inline-flex max-w-full items-center truncate rounded-md px-1.5 py-0.5 text-[10px] font-medium ring-1 ${palette[action.kind]}`}
    >
      {label}
    </span>
  );
}
